import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { RoundIntervals, Rounding } from "@repo/utils";
import { Autocomplete, ClearButton, LevelCheckbox } from "@components";
import SettingsCheckboxList from "./components/SettingsCheckboxList";
import Grid from "@material-ui/core/Grid";
import { ExpandMore } from "@material-ui/icons";
import classnames from "classnames";

export const RoundingSettingsContents = ({
  type,
  level,
  interval,
  intervalPlaceholder,
  usedSections,
  availableSections,
  onTypeChange,
  onLevelChange,
  onIntervalChange,
  onSelectSection,
  className,
  globalSettings,
  showSectionsSettings = true,
  onAdvancedSettingsToggle,
}) => {
  const { t } = useTranslation();
  
  return (
    <Grid
      item container
      wrap="nowrap"
      className={classnames("mt-2 gap-3", className)}
    >
      <Grid
        item container
        className="w-max"
        wrap="nowrap"
        direction="column"
      >
        <Grid
          item container
          alignItems="center"
          className="mb-2 h-20"
        >
          <p>{t("Type")}</p>
        </Grid>
        <Autocomplete
          value={type}
          options={[Rounding.FULL, Rounding.CEIL, Rounding.FLOOR].map(o => ({ id: o, name: t(`rounding.${o}`) }))}
          onChange={(_, v) => onTypeChange(v.id)}
          size="small"
          disableClearable
          style={{ minWidth: 148 }}
        />
      </Grid>
      <Grid
        item container
        className="w-max"
        wrap="nowrap"
        direction="column"
      >
        <Grid
          item container
          alignItems="center"
          className="mb-2 h-20"
        >
          <p>{t("Interval")}</p>
        </Grid>
        <Autocomplete
          value={interval}
          options={[RoundIntervals.QUARTER, RoundIntervals.HALF, RoundIntervals.FULL].map(o => ({ id: o, name: 1/o }))}
          onChange={(_, v) => onIntervalChange(v.id)}
          inputPlaceholder={intervalPlaceholder}
          size="small"
          disableClearable
          style={{ minWidth: 74 }}
        />
      </Grid>
      <Grid
        item container
        className="w-full"
        wrap="nowrap"
        direction="column"
      >
        <Grid
          item container
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          className="mb-2 h-20 gap-2"
        >
          <p>{t("Level")}</p>
          {
            onAdvancedSettingsToggle &&
            <ClearButton
              size="small"
              className="w-max text-sm"
              endIcon={<ExpandMore className={showSectionsSettings ? "rotate-90" : "-rotate-90"} />}
              onClick={onAdvancedSettingsToggle}
            >
              {t("common.advanced_settings")}
            </ClearButton>
          }
        </Grid>
        {
          (
            globalSettings
              ? [-1,0,1,2,3]
              : [0,1,2,3]
          ).map((l, i) => (
            <LevelCheckbox
              key={`level${l}`}
              onCheck={onLevelChange}
              level={l}
              index={i}
              checked={level >= l}
              // disabled={level > maxViewLevel}
            />
          ))
        }
      </Grid>
      {
        globalSettings && showSectionsSettings &&
        <SettingsCheckboxList
          availableItems={availableSections}
          selectedIds={usedSections}
          onSelectItems={onSelectSection}
          title={t("common.apply_to")}
          emptyLabel={t("views.editor.dialogs.automation.empty_sections")}
          selectAllTitle={t("common.all_sections")}
          showSelectAllOnList
          allowSelectAll
          disabled={level === -1}
          hideList
          maxVisible={0}
        />
      }
    </Grid>
  )
}

RoundingSettingsContents.propTypes = {
  type: number,
  level: number,
  interval: number,
  intervalPlaceholder: string,
  usedSections: arrayOf(number),
  availableSections: arrayOf(shape({
    id: string.isRequired,
    name: string.isRequired,
  })),
  onTypeChange: func,
  onLevelChange: func,
  onIntervalChange: func,
  onSelectSection: func,
  className: string,
  globalSettings: bool,
  showSectionsSettings: bool,
  onAdvancedSettingsToggle: func,
}
